/**
 * src/components/spatial/SpatialInspectorPanels.tsx
 *
 * Reusable, provider-agnostic inspection/control panels for the
 * spatial-objects subsystem, modelled on the panels GeoLibre ships
 * (layer list, attribute table, style editor) but implemented purely
 * against our existing `SpatialMapProvider` abstraction — no new infra.
 *
 *   • LayerPanel     — per-layer visibility toggle + opacity slider for
 *                      the managed `gl-spatial-*` render layers.
 *   • AttributeTable — tabular inspector of the currently-loaded spatial
 *                      object features (name / type / height / links),
 *                      with row-click selection.
 *   • StylePanel     — global style overrides (fill colour, opacity,
 *                      vertical exaggeration) applied live via paint
 *                      properties, with a one-click reset to GeoLink's
 *                      data-driven defaults.
 *
 * These are presentational + provider-driven; they hold no data of
 * their own beyond local UI state.
 */

import React, { useMemo, useState } from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';
import type { SpatialObjectFeature } from '../../services/spatial/geoLinkTypes';
import { useDraggablePanel } from './useDraggablePanel';
import { useSpatialObjects } from './SpatialObjectsContext';
import { useMapDataOptional } from './MapDataContext';
import { AGGREGATE_LAYERS } from './mapAggregateLayers';
import { POLICY_STATE_RGB } from './spatialSemantics';
import {
  SPATIAL_LAYER_IDS,
  buildExtrusionColorExpr,
  buildExtrusionOpacityExpr,
  type SpatialLayerId,
} from './spatialLayerUtils';

type LayerKind = 'building' | 'floor' | 'zone' | 'slice';

interface ManagedLayer {
  id: SpatialLayerId;
  label: string;
  /** Whether this is a fill-extrusion layer (vs the point circle layer). */
  extrusion: boolean;
  kind?: LayerKind;
}

const MANAGED_LAYERS: ManagedLayer[] = [
  { id: SPATIAL_LAYER_IDS.building, label: 'Buildings', extrusion: true, kind: 'building' },
  { id: SPATIAL_LAYER_IDS.floor,    label: 'Floors',    extrusion: true, kind: 'floor' },
  { id: SPATIAL_LAYER_IDS.zone,     label: 'Zones',     extrusion: true, kind: 'zone' },
  { id: SPATIAL_LAYER_IDS.slice,    label: 'Slices',    extrusion: true, kind: 'slice' },
  { id: SPATIAL_LAYER_IDS.point,    label: 'Point markers', extrusion: false },
];

const panelStyle: React.CSSProperties = {
  background: 'rgba(20,22,28,0.92)',
  color: '#e8eaf0',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 12,
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
};

const headingStyle: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: '#aab2c5',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 0',
};

// Truncating cell so long names/types can't force a horizontal scrollbar
// in the fixed-layout attribute table.
const ATTR_CELL: React.CSSProperties = {
  padding: '3px 4px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 0,
};

// ─── LayerPanel ──────────────────────────────────────────────────────

export const LayerPanel: React.FC<{
  provider: SpatialMapProvider;
  /** Re-render trigger when layers are (re)added after a style reload. */
  epoch?: number;
  style?: React.CSSProperties;
}> = ({ provider, epoch = 0, style }) => {
  const present = useMemo(
    () => MANAGED_LAYERS.filter((l) => provider.hasLayer(l.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [provider, epoch],
  );

  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(MANAGED_LAYERS.map((l) => [l.id, true])),
  );
  const [opacity, setOpacity] = useState<Record<string, number>>(
    () => Object.fromEntries(
      MANAGED_LAYERS.map((l) => [l.id, l.extrusion && l.kind ? buildExtrusionOpacityExpr(l.kind) : 1]),
    ),
  );

  const toggleVisible = (layer: ManagedLayer) => {
    const next = !visible[layer.id];
    setVisible((v) => ({ ...v, [layer.id]: next }));
    try { provider.setLayerVisibility(layer.id, next); } catch { /* ignore */ }
  };

  const changeOpacity = (layer: ManagedLayer, value: number) => {
    setOpacity((o) => ({ ...o, [layer.id]: value }));
    const prop = layer.extrusion ? 'fill-extrusion-opacity' : 'circle-opacity';
    try { provider.setPaintProperty(layer.id, prop, value); } catch { /* ignore */ }
  };

  if (present.length === 0) return null;

  return (
    <div style={{ ...panelStyle, ...style }}>
      <h4 style={headingStyle}>Layers</h4>
      {present.map((layer) => (
        <div key={layer.id} style={rowStyle}>
          <input
            type="checkbox"
            checked={visible[layer.id] ?? true}
            onChange={() => toggleVisible(layer)}
            title={`Toggle ${layer.label}`}
          />
          <span style={{ flex: 1, minWidth: 78 }}>{layer.label}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={opacity[layer.id] ?? 1}
            onChange={(e) => changeOpacity(layer, Number(e.target.value))}
            disabled={!(visible[layer.id] ?? true)}
            title="Opacity"
            style={{ width: 80 }}
          />
        </div>
      ))}
    </div>
  );
};

// ─── AttributeTable ──────────────────────────────────────────────────

function propStr(props: Record<string, unknown> | undefined, ...keys: string[]): string {
  if (!props) return '—';
  for (const k of keys) {
    const v = props[k];
    if (v != null && v !== '') return String(v);
  }
  return '—';
}

export const AttributeTable: React.FC<{
  features: SpatialObjectFeature[];
  onSelect?: (feature: SpatialObjectFeature) => void;
  style?: React.CSSProperties;
  maxRows?: number;
}> = ({ features, onSelect, style, maxRows = 200 }) => {
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    // Collapse render-tiers to one row per distinct object. The map layer
    // splits a tiered building (e.g. Burj Khalifa) into one Feature per
    // architectural setback, and ids like "<uuid>#t0", "<uuid>#t1" share a
    // single canonical object. Without this the attribute table shows the
    // same building many times. Genuine duplicate OBJECTS (different
    // canonical ids) still each get a row, so dupes remain visible.
    const canonicalId = (f: SpatialObjectFeature): string => {
      const p = (f.properties ?? {}) as Record<string, unknown>;
      for (const key of ['objectId', 'object_id', 'spatial_object_id', 'spatialObjectId', 'id']) {
        const v = p[key];
        if (typeof v === 'string' && v) return v.split('#')[0];
        if (typeof v === 'number') return String(v);
      }
      // Fall back to the feature id (strip any "#tN" tier suffix).
      if (f.id != null) return String(f.id).split('#')[0];
      return '';
    };

    const seen = new Set<string>();
    const deduped: SpatialObjectFeature[] = [];
    for (const f of features) {
      const id = canonicalId(f);
      const key = id || JSON.stringify(f.geometry ?? {});
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(f);
    }

    const q = query.trim().toLowerCase();
    const filtered = q
      ? deduped.filter((f) => {
          const p = f.properties as Record<string, unknown> | undefined;
          return JSON.stringify(p ?? {}).toLowerCase().includes(q);
        })
      : deduped;
    return filtered.slice(0, maxRows);
  }, [features, query, maxRows]);

  return (
    <div style={{ ...panelStyle, ...style }}>
      <h4 style={headingStyle}>Attributes ({features.length})</h4>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Filter features…"
        style={{
          width: '100%', boxSizing: 'border-box', marginBottom: 6,
          padding: '4px 6px', borderRadius: 6, fontSize: 12,
          border: '1px solid rgba(255,255,255,0.15)',
          background: 'rgba(0,0,0,0.3)', color: '#e8eaf0',
        }}
      />
      <div style={{ maxHeight: 220, overflowY: 'auto', overflowX: 'hidden' }}>
        <table style={{ width: '100%', maxWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 11 }}>
          <colgroup>
            <col style={{ width: '44%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr style={{ textAlign: 'left', color: '#aab2c5' }}>
              <th style={ATTR_CELL}>Name</th>
              <th style={ATTR_CELL}>Type</th>
              <th style={ATTR_CELL}>Height</th>
              <th style={ATTR_CELL}>Links</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f, i) => {
              const p = f.properties as Record<string, unknown> | undefined;
              const links: string[] = [];
              if (p?.executionRuleId != null || p?.linkHasExecutionRule === true) links.push('⚡');
              if (p?.contractAddress != null || p?.linkHasContract === true) links.push('📄');
              return (
                <tr
                  key={(f.id != null ? String(f.id) : '') + i}
                  onClick={() => onSelect?.(f)}
                  style={{ cursor: onSelect ? 'pointer' : 'default', borderTop: '1px solid rgba(255,255,255,0.06)' }}
                  title="Fly to / inspect"
                >
                  <td style={ATTR_CELL}>{propStr(p, 'name', 'title')}</td>
                  <td style={ATTR_CELL}>{propStr(p, 'objectType', 'object_type')}</td>
                  <td style={ATTR_CELL}>{propStr(p, 'heightMeters', 'height')}</td>
                  <td style={ATTR_CELL}>{links.join(' ') || '—'}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={4} style={{ padding: '8px 4px', color: '#8890a4' }}>No features.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── StylePanel ──────────────────────────────────────────────────────

export const StylePanel: React.FC<{
  provider: SpatialMapProvider;
  epoch?: number;
  style?: React.CSSProperties;
}> = ({ provider, epoch = 0, style }) => {
  const { renderPipeline, setRenderPipeline } = useSpatialObjects();
  const [colorOverride, setColorOverride] = useState<string>('');
  const [useOverride, setUseOverride] = useState(false);
  const [exaggeration, setExaggeration] = useState(1);

  const extrusionLayers = useMemo(
    () => MANAGED_LAYERS.filter((l) => l.extrusion && l.kind),
    [],
  );

  const applyColor = (hex: string, enabled: boolean) => {
    extrusionLayers.forEach((l) => {
      if (!provider.hasLayer(l.id)) return;
      const value = enabled ? hex : buildExtrusionColorExpr(l.kind as LayerKind);
      try { provider.setPaintProperty(l.id, 'fill-extrusion-color', value); } catch { /* ignore */ }
    });
  };

  const applyExaggeration = (mult: number) => {
    setExaggeration(mult);
    extrusionLayers.forEach((l) => {
      if (!provider.hasLayer(l.id)) return;
      // Scale the data-driven height by the multiplier. We reference the
      // canonical height fields so this stays expression-based and resets
      // cleanly when the multiplier returns to 1.
      const expr: unknown = [
        '*',
        ['coalesce', ['get', 'heightMeters'], ['get', 'height'], 1],
        mult,
      ];
      try { provider.setPaintProperty(l.id, 'fill-extrusion-height', expr); } catch { /* ignore */ }
    });
  };

  const reset = () => {
    setUseOverride(false);
    setColorOverride('');
    setExaggeration(1);
    extrusionLayers.forEach((l) => {
      if (!provider.hasLayer(l.id)) return;
      try {
        provider.setPaintProperty(l.id, 'fill-extrusion-color', buildExtrusionColorExpr(l.kind as LayerKind));
        provider.setPaintProperty(l.id, 'fill-extrusion-height', ['coalesce', ['get', 'heightMeters'], ['get', 'height'], 1]);
        provider.setPaintProperty(l.id, 'fill-extrusion-opacity', buildExtrusionOpacityExpr(l.kind as LayerKind));
      } catch { /* ignore */ }
    });
  };

  // Touch epoch so callers can force a remount after a style reload.
  void epoch;

  const pipelineBtn = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '5px 8px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 11,
    lineHeight: 1.2,
    border: active ? '1px solid #6366f1' : '1px solid rgba(255,255,255,0.15)',
    background: active ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.06)',
    color: '#e8eaf0',
  });

  return (
    <div style={{ ...panelStyle, ...style }}>
      <h4 style={headingStyle}>Style</h4>

      {/* Rendering pipeline. Both paths paint buildings with native Mapbox
          fill-extrusion (accurate per-tier min_height→height). They differ
          in the data source: legacy uses GeoLink /render; Map API 3D uses
          normalized /api/map (+ detail enrichment for tiers). deck.gl only
          draws aggregate layers (agents, rules, …) — not building extrusions. */}
      <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
        <span>Rendering pipeline</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            style={pipelineBtn(renderPipeline === 'legacy')}
            onClick={() => setRenderPipeline('legacy')}
            title="GeoLink /render + native Mapbox 3D — accurate stepped buildings"
          >
            Accurate 3D
            <br />(legacy + native)
          </button>
          <button
            style={pipelineBtn(renderPipeline === 'phase2')}
            onClick={() => setRenderPipeline('phase2')}
            title="Normalized /api/map (+ detail) + native Mapbox 3D — same extrusion accuracy; exercises map-API fetch + aggregate deck layers"
          >
            Map API 3D
            <br />(Map API + native)
          </button>
        </div>
      </div>

      <div style={rowStyle}>
        <input
          type="checkbox"
          checked={useOverride}
          onChange={(e) => {
            const enabled = e.target.checked;
            setUseOverride(enabled);
            applyColor(colorOverride || '#6366f1', enabled);
          }}
        />
        <span style={{ flex: 1 }}>Override fill colour</span>
        <input
          type="color"
          value={colorOverride || '#6366f1'}
          onChange={(e) => {
            setColorOverride(e.target.value);
            if (useOverride) applyColor(e.target.value, true);
          }}
          style={{ width: 32, height: 22, padding: 0, border: 'none', background: 'none' }}
        />
      </div>
      <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
        <span>Vertical exaggeration: {exaggeration.toFixed(1)}×</span>
        <input
          type="range"
          min={0.5}
          max={5}
          step={0.5}
          value={exaggeration}
          onChange={(e) => applyExaggeration(Number(e.target.value))}
        />
        <span style={{ fontSize: 10, opacity: 0.65, lineHeight: 1.3 }}>
          Scales native fill-extrusion height. Keep at 1.0× for true metres
          (tiered buildings like Atlantis The Royal).
        </span>
      </div>
      <button
        onClick={reset}
        style={{
          marginTop: 6, width: '100%', padding: '5px 8px', borderRadius: 6,
          border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer',
          background: 'rgba(255,255,255,0.06)', color: '#e8eaf0', fontSize: 12,
        }}
      >
        Reset to GeoLink defaults
      </button>
    </div>
  );
};

// ─── MapLayersPanel ──────────────────────────────────────────────────
//
// GeoLibre-style layer list for the Phase 2 aggregate layers (country
// policy, execution rules, presence density, agents, drones). Each row is
// a visibility toggle with a colour swatch + live status; a policy-state
// legend sits underneath. Visibility drives both the deck render (via
// `visibleLayerIds`) and the controller's cost-bounded fetching.

const POLICY_LEGEND: Array<{ state: string; label: string }> = [
  { state: 'allowed', label: 'Allowed' },
  { state: 'permitRequired', label: 'Permit required' },
  { state: 'verificationRequired', label: 'Verification' },
  { state: 'adminApprovalRequired', label: 'Admin approval' },
  { state: 'restricted', label: 'Restricted' },
  { state: 'denied', label: 'Denied' },
  { state: 'unsupported', label: 'Unsupported' },
  { state: 'notConfigured', label: 'Not configured' },
];

const rgbCss = (rgb: readonly [number, number, number]) => `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;

export const MapLayersPanel: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
  const mapData = useMapDataOptional();
  if (!mapData) {
    return (
      <div style={{ ...panelStyle, ...style }}>
        <h4 style={headingStyle}>Map layers</h4>
        <div style={{ color: '#8890a4' }}>Map data context unavailable.</div>
      </div>
    );
  }

  const { visibleLayerIds, layerStatus } = mapData.state;

  return (
    <div style={{ ...panelStyle, ...style }}>
      <h4 style={headingStyle}>Map layers</h4>
      {AGGREGATE_LAYERS.map((d) => {
        const on = visibleLayerIds.includes(d.layerId);
        const st = layerStatus[d.layerId];
        const count = mapData.state[d.slice]?.length ?? 0;
        let note = '';
        if (st?.permissionDenied) note = 'no access';
        else if (st?.loading) note = 'loading…';
        else if (st?.error) note = 'error';
        else if (on && st?.empty) note = 'none in view';
        else if (on) note = String(count);
        return (
          <div key={d.layerId} style={{ ...rowStyle, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={on}
              onChange={(e) => mapData.toggleLayer(d.layerId, e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span
              aria-hidden
              style={{
                width: 10, height: 10, borderRadius: 3, marginTop: 3,
                background: d.swatch, flex: '0 0 auto',
                boxShadow: '0 0 0 1px rgba(255,255,255,0.25)',
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>{d.label}</span>
                <span style={{ color: st?.error ? '#f87171' : '#8890a4', fontSize: 11 }}>{note}</span>
              </div>
              <div style={{ color: '#8890a4', fontSize: 11, lineHeight: 1.3 }}>{d.description}</div>
            </div>
          </div>
        );
      })}

      <h4 style={{ ...headingStyle, marginTop: 12 }}>Policy legend</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 10px' }}>
        {POLICY_LEGEND.map((p) => (
          <div key={p.state} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              aria-hidden
              style={{
                width: 10, height: 10, borderRadius: 2, flex: '0 0 auto',
                background: rgbCss((POLICY_STATE_RGB as Record<string, readonly [number, number, number]>)[p.state]),
              }}
            />
            <span style={{ fontSize: 11 }}>{p.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Combined inspector ──────────────────────────────────────────────

export const SpatialInspector: React.FC<{
  provider: SpatialMapProvider;
  features: SpatialObjectFeature[];
  epoch?: number;
  onSelectFeature?: (feature: SpatialObjectFeature) => void;
  style?: React.CSSProperties;
}> = ({ provider, features, epoch, onSelectFeature, style }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'layers' | 'map' | 'attributes' | 'style'>('layers');
  // Default to the bottom-RIGHT so it clears the bottom-left wallet /
  // game panel. Draggable, so the operator can park it anywhere.
  const { containerRef, containerStyle, dragHandleProps } = useDraggablePanel({ bottom: 16, right: 16 });

  return (
    <div
      ref={containerRef}
      style={{
        ...containerStyle,
        // Wider on desktop so all four attribute columns are readable;
        // capped to the viewport so it stays usable on mobile.
        width: open ? 'min(94vw, 400px)' : 'auto',
        maxWidth: open ? '94vw' : undefined,
        ...style,
      }}
    >
      {!open ? (
        <div style={{ ...panelStyle, padding: 0, display: 'flex', alignItems: 'stretch', overflow: 'hidden' }}>
          <span
            {...dragHandleProps}
            title="Drag to move"
            style={{ ...dragHandleProps.style, display: 'flex', alignItems: 'center', padding: '0 6px', color: '#8890a4', borderRight: '1px solid rgba(255,255,255,0.08)' }}
          >⠿</span>
          <button
            data-no-drag
            onClick={() => setOpen(true)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px 12px', fontWeight: 700, color: '#e8eaf0', fontSize: 12 }}
            title="Open spatial inspector"
          >
            🗂 Inspector{features.length ? ` (${features.length})` : ''}
          </button>
        </div>
      ) : (
        <div style={{ ...panelStyle, padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <span
              {...dragHandleProps}
              title="Drag to move"
              style={{ ...dragHandleProps.style, padding: '7px 8px', color: '#8890a4' }}
            >⠿</span>
            {(['layers', 'map', 'attributes', 'style'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  flex: 1, padding: '7px 4px', border: 'none', cursor: 'pointer',
                  background: tab === t ? 'rgba(255,255,255,0.1)' : 'transparent',
                  color: tab === t ? '#fff' : '#aab2c5', fontSize: 11, fontWeight: 700,
                  textTransform: 'capitalize',
                }}
              >
                {t}
              </button>
            ))}
            <button
              onClick={() => setOpen(false)}
              style={{ padding: '7px 10px', border: 'none', cursor: 'pointer', background: 'transparent', color: '#aab2c5' }}
              title="Close"
            >✕</button>
          </div>
          <div style={{ padding: 10 }}>
            {tab === 'layers' && <LayerPanel provider={provider} epoch={epoch} style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }} />}
            {tab === 'map' && <MapLayersPanel style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }} />}
            {tab === 'attributes' && <AttributeTable features={features} onSelect={onSelectFeature} style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }} />}
            {tab === 'style' && <StylePanel provider={provider} epoch={epoch} style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpatialInspector;
